import React from "react";
import Filter from "../../../components/Filter";
import MobileFilterDrawer from "../../../components/mobile/MobileFilterDrawer";
import { useIsMobile } from "../../../hooks/useIsMobile";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { INVERTED_RANGE_MESSAGE, invertedRangeToKeys, rangeInputProps } from "../../../lib/dateRange";

const StatusFilter = ({ filterData, applyFilter, selectedFilters, cities = [], citiesLoading = false }) => {
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [filters, setFilters] = React.useState({...selectedFilters});

  const selectedFiltersKey = JSON.stringify(selectedFilters);
  React.useEffect(() => {
    setFilters({...selectedFilters});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiltersKey]);

  const handleFilterChange = (filterKey, filterValue, e) => {
    const { checked } = e.target;

    // Handler for filter change
    setFilters((prevFilters) => {
      let _filters = { ...prevFilters };
      if (_filters[filterKey]) {
        if (!checked) {
          // Remove from filters
          _filters[filterKey] = _filters[filterKey].filter(
            (_filter) => _filter !== filterValue
          );
        } else {
          // Add to filters — copy, don't push: the array is shared with the
          // `selectedFilters` prop (shallow spread), and mutating it would make
          // Close's "discard back to selectedFilters" keep the unsaved tick.
          _filters[filterKey] = [..._filters[filterKey], filterValue];
        }
      } else {
        // Assign the value to filter key
        _filters[filterKey] = [filterValue];
      }
      return _filters;
    });
  };

  const handleDateFilterChange = (filterKey, filterValue) => {
    setFilters((prevFilters) => {
      let _filters = { ...prevFilters };
      _filters[filterKey] = filterValue
      return _filters
    })
  }

  const handleValueFilterChange = (filterKey, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterKey]: value,
    }));
  };

  const filterLength = () => {
    // Get the number of filters applied based on the category group
    return Object.entries(selectedFilters).reduce((acc, [key, value]) => {
      if (Array.isArray(value) ? value.length : value) {
        return (acc += 1);
      }
      return acc;
    }, 0);
  };

  // A To before its From matches nothing and would silently empty the list.
  const invertedToKeys = invertedRangeToKeys(filterData, filters);

  const handleClose = () => {
    setFilters({...selectedFilters});
  }

  const formContent = (
    <Box pb={2}>
      <FormGroup>
        {filterData.map((filterItem) => (
          <div key={filterItem.title + filterItem.filter_key}>
            <span className="ft-16 bold">{filterItem.title}</span>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: { xs: "wrap", md: "nowrap" },
                gap: { xs: 1, md: 0 },
                mt: 2,
                mb: 3,
              }}
            >
                {filterItem.type === "checkbox" && filterItem.filters?.map((_filter) => (
                  <FormControlLabel
                    key={_filter.value}
                    label={_filter.label}
                    control={
                      <Checkbox
                        name={_filter.value}
                        checked={
                          filters[filterItem.filter_key]?.indexOf(
                            _filter.value
                          ) > -1
                        }
                        onChange={(e) =>
                          handleFilterChange(
                            filterItem.filter_key,
                            _filter.value,
                            e
                          )
                        }
                      />
                    }
                  />
                ))}
                {filterItem.type === "rangeDate" && filters && filterItem.filters?.map((_filter, index) => (
                  <TextField
                    key={_filter.filterKey}
                    inputProps={rangeInputProps(filterItem, index, filters)}
                    error={invertedToKeys.includes(_filter.filterKey)}
                    helperText={invertedToKeys.includes(_filter.filterKey) ? INVERTED_RANGE_MESSAGE : undefined}
                    value={filters[_filter.filterKey] || ''}
                    onChange={(e) =>
                      handleDateFilterChange(
                        _filter.filterKey,
                        e.target.value
                      )
                    }
                    InputLabelProps={{ shrink: true }}
                    label={_filter.label}
                    name={"invoice_date"}
                    type="date"
                    style={{marginRight: '4px'}}
                  />
                ))}
                {filterItem.type === "text" && (
                  <TextField
                    value={filters[filterItem.filter_key] || ""}
                    onChange={(e) =>
                      handleValueFilterChange(filterItem.filter_key, e.target.value)
                    }
                    placeholder={filterItem.placeholder}
                    size="small"
                    fullWidth
                  />
                )}
                {filterItem.type === "select" && (
                  citiesLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Select
                      value={filters[filterItem.filter_key] || ""}
                      onChange={(e) =>
                        handleValueFilterChange(filterItem.filter_key, e.target.value)
                      }
                      displayEmpty
                      size="small"
                      fullWidth
                      renderValue={(selected) =>
                        selected || filterItem.placeholder
                      }
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {cities.map((city) => (
                        <MenuItem key={city.id} value={city.display_name}>
                          {city.display_name}
                        </MenuItem>
                      ))}
                    </Select>
                  )
                )}
            </Box>
          </div>
        ))}
      </FormGroup>
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterListIcon fontSize="small" />}
          onClick={() => setMobileOpen(true)}
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          {`${filterLength() || "No"} filter applied`}
        </Button>
        <MobileFilterDrawer
          open={mobileOpen}
          onClose={() => {
            handleClose();
            setMobileOpen(false);
          }}
          onApply={() => applyFilter(filters)}
          applyDisabled={invertedToKeys.length > 0}
          onReset={() => setFilters({})}
          title="Filter"
        >
          {formContent}
        </MobileFilterDrawer>
      </>
    );
  }

  return (
    <Filter
      customStyle={{ padding: 0 }}
      clearFilter={() => setFilters({})}
      onFilter={() => {
        applyFilter(filters);
      }}
      onClose={handleClose}
      applyDisabled={invertedToKeys.length > 0}
      title={`${filterLength() || "No"} filter applied`}
    >
      {formContent}
    </Filter>
  );
};

export default StatusFilter;
